/**
 * @component QuillEditor
 * @description 리치 텍스트 편집기를 React 제어 방식으로 감싼 공통 에디터 컴포넌트입니다.
 * @usage ContactFormPanel(문의 본문), NewsAdminPanel(뉴스 본문, 이미지 삽입 포함)에서 사용합니다.
 */

import { useEffect, useRef, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const BASE_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'link', 'color', 'align'];

// [기능] 이미지 허용 여부에 따라 Quill에 넘길 포맷 화이트리스트를 만듭니다.
// [기술] Quill은 formats 배열을 scroll.whitelist로 사용합니다. 여기에 'image'가 없으면
//        (1) 툴바의 이미지 버튼에 클릭 리스너가 아예 연결되지 않고
//        (2) insertEmbed로 넣은 이미지가 조용히 무시됩니다.
//        따라서 이미지 기능을 쓰려면 이 배열에 반드시 'image'가 포함돼야 합니다.
function buildFormats(allowImages) {
  return allowImages ? [...BASE_FORMATS, 'image'] : BASE_FORMATS;
}

const getInlineFormatsForEnter = (formats) => {
  const nextFormats = {};
  ['bold', 'italic', 'underline', 'color'].forEach((key) => {
    if (formats[key]) {
      nextFormats[key] = formats[key];
    }
  });
  return nextFormats;
};

// [기능] 툴바 HTML을 만듭니다. allowImages가 true일 때만 이미지 버튼을 포함합니다.
// [비즈니스] 문의 폼에는 이미지 업로드가 필요 없고 허용해서도 안 되므로 기본값은 false입니다.
function buildToolbarHtml(allowImages) {
  const imageButton = allowImages
    ? '<button type="button" class="ql-image" aria-label="image"></button>'
    : '';

  return [
    '<span class="ql-formats">',
    '  <button type="button" class="ql-bold" aria-label="bold"></button>',
    '  <button type="button" class="ql-italic" aria-label="italic"></button>',
    '  <button type="button" class="ql-underline" aria-label="underline"></button>',
    '</span>',
    '<span class="ql-formats">',
    '  <button type="button" class="ql-list" value="ordered" aria-label="ordered list"></button>',
    '  <button type="button" class="ql-list" value="bullet" aria-label="bullet list"></button>',
    '</span>',
    '<span class="ql-formats">',
    '  <button type="button" class="ql-link" aria-label="link"></button>',
    `  ${imageButton}`,
    '</span>',
    '<span class="ql-formats">',
    '  <select class="ql-color" aria-label="color"></select>',
    '</span>',
    '<span class="ql-formats">',
    '  <select class="ql-align" aria-label="align"></select>',
    '</span>',
    '<span class="ql-formats">',
    '  <button type="button" class="ql-clean" aria-label="clean"></button>',
    '</span>',
  ].join('\n');
}

// [기능] 관리자가 입력한 주소를 저장 가능한 링크 URL로 정규화합니다.
//        허용되지 않는 형식이면 null을 반환합니다.
// [비즈니스] 서버 정화기가 http·https·mailto 외의 주소를 모두 제거하므로,
//            입력 시점에 같은 기준으로 걸러 "저장했더니 링크가 사라진" 상황을 막습니다.
//            'example.com'처럼 스킴 없이 적는 경우가 많아 https를 자동으로 붙입니다.
function normalizeLinkUrl(raw) {
  const value = String(raw || '').trim();
  if (!value) return null;

  // [기술] 스크립트 실행이나 데이터 URL로 들어오는 우회 입력을 차단합니다.
  if (/^(javascript|data|vbscript|file):/i.test(value)) return null;
  // [기술] '//example.com' 형태는 스킴 검사를 우회하는 경로라 허용하지 않습니다.
  if (value.startsWith('//')) return null;

  if (/^mailto:/i.test(value)) return value;
  if (/^https?:\/\//i.test(value)) return value;
  // [비즈니스] 메일 주소만 적은 경우 mailto 링크로 만들어 줍니다.
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return `mailto:${value}`;

  return `https://${value}`;
}

// [기능] 에디터의 현재 내용을 저장·비교용 HTML 문자열로 읽습니다.
// [기술] Quill은 내용이 비어 있어도 '<p><br></p>'를 남기므로 빈 문자열로 정규화합니다.
//        읽는 곳마다 규칙이 다르면 상위 상태와 에디터 내용이 어긋나 본문이 다시 삽입됩니다.
function readEditorHtml(editor) {
  const html = editor.root.innerHTML;
  return html === '<p><br></p>' ? '' : html;
}

export default function QuillEditor({ value, onChange, placeholder, allowImages = false, onImageUpload, useLinkDialog = false }) {
  const toolbarRef = useRef(null);
  const hostRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onImageUploadRef = useRef(onImageUpload);
  const valueRef = useRef(value);
  // [기술] 붙여넣기 이미지 처리 중 발생하는 text-change로 같은 작업이 재진입하는 것을 막습니다.
  const isProcessingPasteRef = useRef(false);
  // [기술] 우리가 마지막으로 상위에 올려 보낸 HTML입니다.
  //        prop으로 돌아온 값이 그 메아리인지, 외부에서 새로 내려준 값인지 구분합니다.
  const lastEmittedRef = useRef(value);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  // [기술] 링크 입력창의 상태입니다. null이면 닫힌 상태입니다.
  const [linkForm, setLinkForm] = useState(null);
  const [linkError, setLinkError] = useState('');

  useEffect(() => {
    // [콜백 동기화]: 최신 onChange 핸들러를 ref에 유지해 에디터 이벤트에서 안전하게 호출합니다.
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    // [기술] 업로드 콜백도 ref로 고정합니다. 초기화 이펙트의 의존성에 함수를 직접 넣으면
    //        부모가 인라인 화살표 함수를 넘길 때 매 렌더마다 에디터가 파괴·재생성됩니다.
    onImageUploadRef.current = onImageUpload;
  }, [onImageUpload]);

  useEffect(() => {
    // [상태 동기화]: 외부 value prop을 ref에 저장해 초기화 시점에 참조합니다.
    valueRef.current = value;
  }, [value]);

  // [기능] 링크 입력창에서 '적용'을 눌렀을 때 선택 영역에 링크를 겁니다.
  //        선택한 텍스트가 없으면 표시 문구를 새로 넣고 링크를 겁니다.
  const applyLink = () => {
    const editor = quillRef.current;
    if (!editor || !linkForm) return;

    const url = normalizeLinkUrl(linkForm.url);
    if (!url) {
      setLinkError('http, https, mailto 주소만 넣을 수 있습니다.');
      return;
    }

    const { index, length } = linkForm.range;
    const displayText = String(linkForm.text || '').trim() || url;
    const currentText = length > 0 ? editor.getText(index, length) : '';

    if (length > 0 && displayText === currentText) {
      editor.formatText(index, length, 'link', url, 'user');
      editor.setSelection(index + length, 0, 'silent');
    } else {
      // [비즈니스] 표시 문구를 바꿨거나 선택 영역이 없으면 문구를 새로 넣고 링크를 겁니다.
      if (length > 0) editor.deleteText(index, length, 'user');
      editor.insertText(index, displayText, { link: url }, 'user');
      editor.setSelection(index + displayText.length, 0, 'silent');
    }

    setLinkForm(null);
    setLinkError('');
  };

  // [기능] 현재 링크를 해제해 일반 텍스트로 되돌립니다.
  const removeLink = () => {
    const editor = quillRef.current;
    if (!editor || !linkForm) return;
    const { index, length } = linkForm.range;
    if (length > 0) editor.formatText(index, length, 'link', false, 'user');
    setLinkForm(null);
    setLinkError('');
  };

  // [기능] 에디터 내용을 상위 상태로 올려 보내고, 방금 올린 값을 기억해 둡니다.
  const emitChange = (editor) => {
    const nextHtml = readEditorHtml(editor);
    lastEmittedRef.current = nextHtml;
    onChangeRef.current(nextHtml);
  };

  useEffect(() => {
    if (!hostRef.current || !toolbarRef.current || quillRef.current) return undefined;

    const hostElement = hostRef.current;
    const toolbarElement = toolbarRef.current;
    // Quill controls the toolbar DOM itself, so we hand it a stable element to avoid duplicate toolbars in Strict Mode.
    toolbarElement.innerHTML = buildToolbarHtml(allowImages);

    // [기능] 이미지 버튼 클릭 시 파일을 선택받아 업로드하고 커서 위치에 삽입합니다.
    // [기술] 파일 입력을 DOM에 영구히 붙여 두면 StrictMode의 이중 마운트에서 요소가 누적되고
    //        선택 창이 두 번 뜰 수 있어, 클릭할 때마다 임시로 만들고 버립니다.
    const handleImageButton = () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png,image/webp,image/gif';

      input.onchange = async () => {
        const file = input.files && input.files[0];
        if (!file || !onImageUploadRef.current) return;

        const editor = quillRef.current;
        if (!editor) return;

        // [기술] 삽입 위치는 업로드 전에 확보합니다. 파일 선택 창이 열리는 동안 포커스를 잃어
        //        업로드가 끝난 뒤에는 선택 영역이 사라져 있을 수 있습니다.
        const range = editor.getSelection(true) || { index: editor.getLength() };

        setUploadError('');
        setIsUploading(true);
        // [비즈니스] 업로드 중 추가 입력이 들어오면 삽입 위치가 어긋나므로 입력을 잠급니다.
        editor.enable(false);

        let uploadedUrl = null;
        try {
          uploadedUrl = await onImageUploadRef.current(file);
        } catch (error) {
          // [비즈니스] alert은 모바일에서 화면을 가로막고 입력 흐름을 끊으므로
          //            에디터 아래 인라인 메시지로 알립니다.
          setUploadError(error.message || '이미지 업로드에 실패했습니다.');
        } finally {
          // [기술] 삽입보다 먼저 입력 잠금을 반드시 해제해야 합니다.
          //        Quill은 strict 모드(기본값)에서 비활성 상태의 'user' 편집을 예외 없이
          //        조용히 버립니다. 잠근 채로 insertEmbed를 호출하면 업로드는 성공했는데
          //        본문에는 아무것도 나타나지 않습니다.
          editor.enable(true);
          setIsUploading(false);
        }

        if (uploadedUrl) {
          editor.insertEmbed(range.index, 'image', uploadedUrl, 'user');
          editor.setSelection(range.index + 1, 0, 'silent');
        }
      };

      input.click();
    };

    // [기능] 링크 버튼을 누르면 주소 입력창을 엽니다.
    //        커서가 기존 링크 안에 있으면 그 링크의 주소와 문구를 미리 채워 수정할 수 있게 합니다.
    // [비즈니스] Quill 기본 링크 툴팁은 에디터 바깥쪽에 붙어 좁은 화면에서 화면 밖으로 밀려납니다.
    //            관리자가 확실히 쓸 수 있도록 자체 입력창으로 대체합니다.
    const handleLinkButton = () => {
      const editorInstance = quillRef.current;
      if (!editorInstance) return;

      const range = editorInstance.getSelection(true) || { index: editorInstance.getLength() - 1, length: 0 };
      const LinkFormat = Quill.import('formats/link');
      // [기술] 커서 위치를 감싸고 있는 링크 blot을 찾아 기존 링크 수정인지 판별합니다.
      const [linkBlot] = editorInstance.scroll.descendant(LinkFormat, range.index);

      if (linkBlot) {
        const index = editorInstance.getIndex(linkBlot);
        setLinkForm({
          url: LinkFormat.formats(linkBlot.domNode) || '',
          text: linkBlot.domNode.textContent || '',
          range: { index, length: linkBlot.length() },
          hasExisting: true,
        });
      } else {
        setLinkForm({
          url: '',
          text: range.length > 0 ? editorInstance.getText(range.index, range.length) : '',
          range: { index: range.index, length: range.length },
          hasExisting: false,
        });
      }
      setLinkError('');
    };

    // [기능] 본문에 삽입된 data: 형식 이미지를 서버에 업로드하고 /uploads 경로로 교체합니다.
    // [비즈니스] 워드나 웹페이지에서 본문을 붙여넣으면 이미지가 data: 형식으로 따라 들어옵니다.
    //            서버는 저장 시 data: 이미지를 제거하므로, 그대로 두면 관리자에게는
    //            "저장했더니 이미지만 사라진" 것으로 보입니다. 붙여넣는 즉시 업로드해 이를 막습니다.
    // [기술] DOM을 직접 수정하면 에디터 내부 모델과 어긋나므로, 해당 이미지의 위치를 찾아
    //        Quill API로 교체합니다.
    const replacePastedImages = async () => {
      const editorInstance = quillRef.current;
      if (!editorInstance || !onImageUploadRef.current || isProcessingPasteRef.current) return;

      const pastedImages = editorInstance.root.querySelectorAll('img[src^="data:"]');
      if (pastedImages.length === 0) return;

      isProcessingPasteRef.current = true;
      setUploadError('');
      setIsUploading(true);
      try {
        for (const image of pastedImages) {
          const blob = await (await fetch(image.getAttribute('src'))).blob();
          const extension = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
          const file = new File([blob], `pasted.${extension}`, { type: blob.type || 'image/png' });
          const url = await onImageUploadRef.current(file);

          const blot = Quill.find(image);
          if (!blot) continue;
          const index = editorInstance.getIndex(blot);
          editorInstance.deleteText(index, 1, 'silent');
          editorInstance.insertEmbed(index, 'image', url, 'silent');
        }
        emitChange(editorInstance);
      } catch (error) {
        // [비즈니스] 업로드에 실패한 붙여넣기 이미지는 저장 시 어차피 제거되므로,
        //            에디터에서도 함께 제거해 화면과 저장 결과가 어긋나지 않게 합니다.
        editorInstance.root.querySelectorAll('img[src^="data:"]').forEach((image) => {
          const blot = Quill.find(image);
          if (blot) editorInstance.deleteText(editorInstance.getIndex(blot), 1, 'silent');
        });
        emitChange(editorInstance);
        setUploadError(error.message || '붙여넣은 이미지를 업로드하지 못했습니다.');
      } finally {
        setIsUploading(false);
        isProcessingPasteRef.current = false;
      }
    };

    const editor = new Quill(hostElement, {
      theme: 'snow',
      modules: {
        toolbar: {
          container: toolbarElement,
          // [기술] 핸들러는 반드시 생성자 옵션으로 넘겨야 합니다. Quill의 Toolbar는
          //        생성자에서 handlers를 먼저 등록한 뒤 버튼에 리스너를 붙이므로,
          //        생성 이후 addHandler로 추가하면 리스너 연결 단계가 이미 지나가 동작하지 않습니다.
          handlers: {
            ...(allowImages ? { image: handleImageButton } : {}),
            ...(useLinkDialog ? { link: handleLinkButton } : {}),
          },
        },
        keyboard: {
          bindings: {
            preserveInlineEnter: {
              key: 13,
              shiftKey: false,
              collapsed: true,
              handler(range, context) {
                const format = context?.format || this.quill.getFormat(range.index, 0);
                const hasInlineStyle = !!(format.bold || format.italic || format.underline || format.color);
                const hasBlockStyle = !!(format.list || format.blockquote || format['code-block'] || format.header);

                if (!hasInlineStyle || hasBlockStyle) {
                  return true;
                }

                const nextFormats = getInlineFormatsForEnter(format);
                this.quill.insertText(range.index, '\n', 'user');
                this.quill.setSelection(range.index + 1, 0, 'silent');
                // [기술] 줄바꿈 문자에 인라인 서식을 실어 보내도 다음에 입력되는 글자에는 적용되지 않습니다.
                //        새 줄로 커서를 옮긴 뒤 커서 서식을 다시 지정해야 이어서 타이핑하는 글자가
                //        같은 색·굵기를 유지합니다.
                Object.entries(nextFormats).forEach(([name, formatValue]) => {
                  this.quill.format(name, formatValue, 'silent');
                });
                return false;
              },
            },
          },
        },
      },
      formats: buildFormats(allowImages),
      placeholder,
    });

    const handleTextChange = () => {
      // [상태 변경]: Quill 내부 HTML을 상위 상태로 전달해 폼과 동기화합니다.
      emitChange(editor);
      // [기술] 붙여넣기·드래그드롭 모두 text-change를 거치므로 여기서 한 번만 처리하면 됩니다.
      if (allowImages) replacePastedImages();
    };

    quillRef.current = editor;
    if (valueRef.current) {
      editor.clipboard.dangerouslyPasteHTML(valueRef.current);
    }
    editor.on('text-change', handleTextChange);

    return () => {
      editor.off('text-change', handleTextChange);
      quillRef.current = null;
      toolbarElement.innerHTML = '';
      hostElement.innerHTML = '';
      // [기술] Quill이 host에 붙인 ql-container/ql-snow 클래스를 되돌립니다.
      //        남겨 두면 재초기화 시 클래스가 중첩돼 스타일이 어긋납니다.
      hostElement.className = 'quill-editor-host';
    };
  }, [placeholder, allowImages, useLinkDialog]);

  useEffect(() => {
    const editor = quillRef.current;
    if (!editor) return;

    // [편집 경험 보호] 우리가 올려 보낸 값이 그대로 돌아온 것이면 아무것도 하지 않습니다.
    // [기술] 이 확인이 없으면 상위 상태를 거쳐 돌아온 값과 에디터 내용이 미세하게 다를 때마다
    //        본문 전체를 다시 붙여넣습니다. 그때 문서가 새로 만들어지며 커서와 스크롤이 맨 위로 튀어,
    //        붙여넣기 직후 내용이 위로 올라간 것처럼 보입니다.
    if (value === lastEmittedRef.current) return;

    const currentHtml = readEditorHtml(editor);
    if (value === currentHtml) return;

    // [편집 경험 보호]: 외부에서 새 값을 내려준 경우(기사 수정 로드, 폼 초기화)에만 본문을 교체합니다.
    const selection = editor.getSelection();
    editor.clipboard.dangerouslyPasteHTML(value || '');
    lastEmittedRef.current = value || '';
    if (selection) {
      editor.setSelection(selection);
    }
  }, [value]);

  return (
    <div className={`quill-editor-shell${isUploading ? ' is-uploading' : ''}`}>
      <div className="quill-editor-toolbar" ref={toolbarRef} />
      <div className="quill-editor-host" ref={hostRef} />
      {isUploading && <p className="quill-editor-status">이미지 업로드 중...</p>}
      {uploadError && <p className="quill-editor-error">{uploadError}</p>}

      {/* [비즈니스] 링크 주소와 표시 문구를 한곳에서 입력·수정·해제할 수 있게 합니다. */}
      {linkForm && (
        <div className="quill-link-form">
          <div className="quill-link-fields">
            <label className="quill-link-field">
              <span className="quill-link-label">링크 주소</span>
              <input
                type="text"
                className="admin-input"
                value={linkForm.url}
                onChange={(e) => {
                  setLinkForm((prev) => ({ ...prev, url: e.target.value }));
                  if (linkError) setLinkError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyLink();
                  }
                  if (e.key === 'Escape') setLinkForm(null);
                }}
                placeholder="example.com 또는 https://example.com"
                autoFocus
              />
            </label>

            <label className="quill-link-field">
              <span className="quill-link-label">표시 문구</span>
              <input
                type="text"
                className="admin-input"
                value={linkForm.text}
                onChange={(e) => setLinkForm((prev) => ({ ...prev, text: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyLink();
                  }
                  if (e.key === 'Escape') setLinkForm(null);
                }}
                placeholder="본문에 보일 글자 (비우면 주소가 그대로 표시됩니다)"
              />
            </label>
          </div>

          {linkError && <p className="quill-editor-error">{linkError}</p>}

          <div className="quill-link-actions">
            {/* [비즈니스] 기존 링크를 편집할 때만 해제 버튼을 노출합니다. */}
            {linkForm.hasExisting && (
              <button type="button" className="admin-btn admin-btn-danger" onClick={removeLink}>
                링크 해제
              </button>
            )}
            <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setLinkForm(null)}>
              취소
            </button>
            <button type="button" className="admin-btn admin-btn-primary" onClick={applyLink}>
              적용
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
